import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { TenantStatusUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type TenantStatusUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name: string;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  nameError?: string;
  description_es: string;
  description_esSelectedValue?: { displayId: string; id: string } | null;
  onDescription_esChange?: (value: string) => void;
  description_esError?: string;
  description_en: string;
  description_enSelectedValue?: { displayId: string; id: string } | null;
  onDescription_enChange?: (value: string) => void;
  description_enError?: string;
  description_pt: string;
  description_ptSelectedValue?: { displayId: string; id: string } | null;
  onDescription_ptChange?: (value: string) => void;
  description_ptError?: string;
  blocksAccess: boolean;
  blocksAccessSelectedValue?: { displayId: string; id: string } | null;
  onBlocksAccessChange?: (value: boolean) => void;
  blocksAccessError?: string;
  tenantStatusUpdateStore: TenantStatusUpdateStore;
};

export const TenantStatusUpdateForm: React.FC<TenantStatusUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      description_es = "",
      description_esError,
      onDescription_esChange = () => {},
      description_en = "",
      description_enError,
      onDescription_enChange = () => {},
      description_pt = "",
      description_ptError,
      onDescription_ptChange = () => {},
      blocksAccess = false,
      blocksAccessError,
      onBlocksAccessChange = () => {},
      tenantStatusUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="TenantStatus"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "description_es":
                    onDescription_esChange?.(valor);
                    break;
                  case "description_en":
                    onDescription_enChange?.(valor);
                    break;
                  case "description_pt":
                    onDescription_ptChange?.(valor);
                    break;
                  case "blocksAccess":
                    onBlocksAccessChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={tenantStatusUpdateStore.getCardErrors("null")}
              title={langStore.groupTenantStatusLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(tenantStatusUpdateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    tenantStatusUpdateStore.warnings["name"]?.message
                  }
                  label={langStore.TenantStatusNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.TenantStatusNameDescription}
                />
                <InputString
                  value={description_es}
                  error={Boolean(description_esError)}
                  warning={
                    !description_esError &&
                    Boolean(tenantStatusUpdateStore.warnings["description_es"])
                  }
                  helperText={
                    description_esError ||
                    tenantStatusUpdateStore.warnings["description_es"]?.message
                  }
                  label={langStore.TenantStatusDescriptionLabel + " (es)"}
                  onChange={(value: string) => onDescription_esChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantStatusDescriptionDescription}
                />
                <InputString
                  value={description_en}
                  error={Boolean(description_enError)}
                  warning={
                    !description_enError &&
                    Boolean(tenantStatusUpdateStore.warnings["description_en"])
                  }
                  helperText={
                    description_enError ||
                    tenantStatusUpdateStore.warnings["description_en"]?.message
                  }
                  label={langStore.TenantStatusDescriptionLabel + " (en)"}
                  onChange={(value: string) => onDescription_enChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantStatusDescriptionDescription}
                />
                <InputString
                  value={description_pt}
                  error={Boolean(description_ptError)}
                  warning={
                    !description_ptError &&
                    Boolean(tenantStatusUpdateStore.warnings["description_pt"])
                  }
                  helperText={
                    description_ptError ||
                    tenantStatusUpdateStore.warnings["description_pt"]?.message
                  }
                  label={langStore.TenantStatusDescriptionLabel + " (pt)"}
                  onChange={(value: string) => onDescription_ptChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantStatusDescriptionDescription}
                />
                <InputBoolean
                  value={blocksAccess}
                  error={Boolean(blocksAccessError)}
                  helperText={
                    blocksAccessError ||
                    tenantStatusUpdateStore.warnings["blocksAccess"]?.message
                  }
                  label={langStore.TenantStatusBlocksAccessLabel}
                  description={langStore.TenantStatusBlocksAccessDescription}
                  onChange={(value: boolean) => onBlocksAccessChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
